import React, { useState, useEffect, useContext } from 'react'
import {
  Menu,
  MenuButton,
  MenuPopover,
  MenuItems,
  MenuItem,
  MenuLink,
} from '@reach/menu-button'
import PropTypes from 'prop-types'
import { positionMatchWidth, positionRight } from '@reach/popover'
import { useId } from '@reach/auto-id'
import classNames from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { Button } from 'library'
import '@reach/menu-button/styles.css'
import styles from './styles.module.scss'

const childrenPropType = PropTypes.oneOfType([
  PropTypes.arrayOf(PropTypes.node),
  PropTypes.node,
])

const SelectMenuContext = React.createContext()
const useSelectMenuContext = () => useContext(SelectMenuContext)

export const SelectMenu = ({ defaultValue, children, align, ...props }) => {
  const [dropdownValue, setDropdownValue] = useState(defaultValue)

  // This might not be a good idea
  // but set the default value once detected and we have no dropdown value
  useEffect(() => {
    if (!dropdownValue && defaultValue) setDropdownValue(defaultValue)
  }, [defaultValue, dropdownValue])

  const [selectedItems, setSelectedItems] = useState([])
  const [menuPosition, setMenuPosition] = useState(align)

  const context = {
    dropdownValue,
    setDropdownValue,
    selectedItems,
    setSelectedItems,
    menuPosition,
  }

  return (
    <SelectMenuContext.Provider value={context}>
      <SelectMenuContents {...props}>{children}</SelectMenuContents>
    </SelectMenuContext.Provider>
  )
}

SelectMenu.displayName = 'SelectMenu'

SelectMenu.propTypes = {
  defaultValue: PropTypes.string,
  children: childrenPropType.isRequired,
  align: PropTypes.string,
}

SelectMenu.defaultProps = {
  defaultValue: undefined,
  align: 'left',
}

const SelectMenuContents = ({ children, className, items, ...props }) => {
  const { dropdownValue } = useSelectMenuContext()

  if (items) {
    const getSelectedValue = items.find((item) => item.value === dropdownValue)
    const selectedName = getSelectedValue && getSelectedValue.name
    return (
      <Menu className={classNames(styles.menu, className)} {...props}>
        <SelectToggle>{selectedName || children}</SelectToggle>
        <SelectList>
          {items.map((item) => {
            if (item.value) {
              return (
                <SelectItem key={item.name} value={item.value}>
                  {item.name}
                </SelectItem>
              )
            }
            if (item.url) {
              return (
                <SelectLink key={item.name} href={item.url}>
                  {item.name}
                </SelectLink>
              )
            }
            return false
          })}
        </SelectList>
      </Menu>
    )
  }

  return (
    <Menu className={classNames(styles.menu, className)} {...props}>
      {React.Children.map(children, (child) => {
        if (
          React.isValidElement(child) &&
          child.type.displayName &&
          child.type.displayName.startsWith('Select')
        ) {
          return child
        }
        return console.error(
          `${child.type.displayName} is an incorrect element. Use only SelectToggle and SelectList`
        )
      })}
    </Menu>
  )
}

SelectMenuContents.displayName = 'SelectMenuContents'

SelectMenuContents.propTypes = {
  children: childrenPropType.isRequired,
  className: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
      url: PropTypes.string,
    })
  ),
}

SelectMenuContents.defaultProps = {
  className: undefined,
  items: undefined,
}

export const SelectToggle = ({ as, children, icon, className, ...props }) => {
  const autoId = useId()
  const { dropdownValue, selectedItems } = useSelectMenuContext()
  const getSelectedValue =
    selectedItems && selectedItems.find((item) => item.value === dropdownValue)
  const selectedName = getSelectedValue && getSelectedValue.children

  const hasSelectIcon = React.Children.toArray(children).some(
    (child) => child.type?.displayName === 'SelectIcon'
  )

  return (
    <MenuButton
      as={as}
      id={autoId}
      className={classNames(styles.selectToggle, className)}
      {...props}
    >
      <span>
        {children || selectedName}
        {!hasSelectIcon && <SelectIcon icon={icon} />}
      </span>
    </MenuButton>
  )
}

SelectToggle.displayName = 'SelectToggle'

SelectToggle.propTypes = {
  as: PropTypes.elementType,
  children: childrenPropType.isRequired,
  icon: PropTypes.string,
  className: PropTypes.string,
}

SelectToggle.defaultProps = {
  as: Button,
  icon: 'caret-down',
  className: undefined,
}

export const SelectIcon = ({ icon, className, ...props }) => (
  <FontAwesomeIcon
    icon={icon}
    className={classNames(styles.icon, className)}
    {...props}
  />
)

SelectIcon.displayName = 'SelectIcon'

SelectIcon.propTypes = {
  className: PropTypes.string,
  icon: PropTypes.string,
}

SelectIcon.defaultProps = {
  className: undefined,
  icon: 'caret-down',
}

export const SelectList = React.forwardRef(function SelectMenuList(
  { children, portal = true, ...props },
  forwardedRef
) {
  const { setSelectedItems, menuPosition } = useSelectMenuContext()
  useEffect(() => {
    let newSelectedItems = []
    children.forEach((child) => {
      newSelectedItems = [...newSelectedItems, child.props]
    })
    setSelectedItems(newSelectedItems)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  let _menuPosition
  if (menuPosition === 'matchWidth') {
    _menuPosition = positionMatchWidth
  }
  if (menuPosition === 'right') {
    _menuPosition = positionRight
  }
  return (
    <MenuPopover portal={portal} position={_menuPosition}>
      <MenuItems {...props} ref={forwardedRef} data-reach-menu-list="">
        {React.Children.map(children, (child) => {
          if (
            React.isValidElement(child) &&
            child.type.displayName &&
            (child.type.displayName === 'SelectItem' ||
              child.type.displayName === 'SelectLink')
          ) {
            return child
          }
          // eslint-disable-next-line no-console
          return console.error(
            `${child.type.displayName} is an incorrect element. Use only SelectItem or SelectLink`
          )
        })}
      </MenuItems>
    </MenuPopover>
  )
})

SelectList.displayName = 'SelectList'

SelectList.propTypes = {
  children: childrenPropType.isRequired,
  className: PropTypes.string,
}

SelectList.defaultProps = {
  className: undefined,
}

export const SelectItem = ({
  children,
  className,
  as,
  value,
  onSelect,
  ...props
}) => {
  const { dropdownValue, setDropdownValue } = useSelectMenuContext()
  const isActive = !!value && dropdownValue === value

  const handleClick = () => {
    setDropdownValue(value)
    if (onSelect) onSelect()
  }

  return (
    <MenuItem
      as={as}
      onSelect={handleClick}
      className={classNames(
        styles.menuItem,
        {
          [styles.isActive]: isActive,
        },
        className
      )}
      {...props}
    >
      {children}
    </MenuItem>
  )
}

SelectItem.displayName = 'SelectItem'

SelectItem.propTypes = {
  children: childrenPropType.isRequired,
  className: PropTypes.string,
  as: PropTypes.elementType,
  value: PropTypes.string,
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
}

SelectItem.defaultProps = {
  className: undefined,
  as: undefined,
  value: undefined,
  onSelect: undefined,
  disabled: undefined,
}

export const SelectLink = ({ children, className, as, onSelect, ...props }) => {
  const handleClick = () => {
    if (onSelect) onSelect()
  }
  // const isActive

  return (
    <MenuLink
      as={as}
      onSelect={handleClick}
      className={classNames(
        styles.menuItem,
        // {
        //   [styles.isActive]: isActive,
        // },
        className
      )}
      {...props}
    >
      {children}
    </MenuLink>
  )
}

SelectLink.displayName = 'SelectLink'

SelectLink.propTypes = {
  children: childrenPropType.isRequired,
  className: PropTypes.string,
  as: PropTypes.elementType,
  onSelect: PropTypes.func,
}

SelectLink.defaultProps = {
  className: undefined,
  as: 'a',
  onSelect: undefined,
}
